import { isEmpty } from 'lodash-es';
import { useCallback, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { autocompleteClientName, ClientDTO } from 'wisestep-api';

import { useWorkspaceContext } from '../../context';
import { showingParentClient, getAssignedClientList } from '../../features/jobs/helper';

import ClientAutoCompleteInput from './ClientAutoCompleteInput';

export interface IParentClient {
  parent: ClientDTO;
}

interface IClientAutoCompleteFilter {
  name: string;
  showMandatorySymbol?: boolean;
  clientIdFieldName?: string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  onNewValueCreate?: (data: string) => void;
  textForAddingNew?: string;
  selectedClientData?: (data: string) => void;
  isPlacement?: boolean;
  onStatusUpdateValue?: (id: string) => void;
  isInvoice?: boolean;
}

const defaultOptions: string[] = [];

const ClientAutoCompleteFilter = ({
  name,
  showMandatorySymbol = false,
  clientIdFieldName,
  placeholder = 'Eg: Infosys',
  label = 'Company Name (Parent -> Child)',
  disabled = false,
  textForAddingNew,
  onNewValueCreate,
  selectedClientData,
  isPlacement = false,
  onStatusUpdateValue,
  isInvoice,
}: IClientAutoCompleteFilter) => {
  const clientList = useRef<ClientDTO[]>([]);

  const { setValue } = useFormContext();
  const currentUser = useWorkspaceContext();
  const isHead = currentUser.role == 'Head';

  const onChange = (event) => {
    const clientId = event.split('|')[3];
    const foundClient = clientList.current?.filter((client) => client.value === clientId);
    selectedClientData && selectedClientData(foundClient[0].value as string);
    setValue(name, event.includes('->') ? event.split('->')[1].trim() : event);

    if (clientIdFieldName && !isEmpty(foundClient))
      setValue(clientIdFieldName, foundClient[0]?.value);
  };

  const onClientNameInputKey = useCallback(
    async (client: string) => {
      const result = await autocompleteClientName(
        `${encodeURIComponent(
          client
            ?.trim()
            .split(' ')
            .map((clientName) => (isEmpty(clientName) ? '' : clientName + ':*'))
            .join(' & ')
        )}`
      );

      if (result?.data.length > 0) {
        if (!isHead && isPlacement) {
          const getClientList = await getAssignedClientList();
          const resultSet: any[] = [];
          result.data.forEach((data) => {
            if (getClientList.has(data.value)) {
              resultSet.push(data);
            }
          });
          clientList.current = resultSet;
          return showingParentClient(resultSet);
        } else {
          clientList.current = result.data;
          return showingParentClient(result.data);
        }
      }
      return [];
    },
    [isHead, isPlacement]
  );

  return (
    <ClientAutoCompleteInput
      label={label}
      name={name}
      key={name}
      options={defaultOptions}
      placeholder={placeholder}
      getOptions={onClientNameInputKey}
      showMandatorySymbol={showMandatorySymbol}
      textForAddingNew={textForAddingNew}
      onChange={onChange}
      onNewValueCreate={onNewValueCreate}
      disable={disabled}
      requireSelection={false}
      onStatusUpdateValue={onStatusUpdateValue}
      isInvoice={isInvoice}
    />
  );
};

export default ClientAutoCompleteFilter;
